import React, { useCallback, useContext, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton } from "@mui/material";
import { Rnd } from "react-rnd";
import { makeStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()(() => ({
  pipResizableContent: {
    zIndex: 9999,
    background: "#202429",
    borderRadius: 4,
    color: colors.white,
  },
  closeButton: {
    position: "absolute",
    top: 4,
    right: 4,
  },
}));

interface Helpers {
  open: (wherebyRoomId: string) => void;
  close: () => void;
}

const GlobalPictureInPictureContext = React.createContext<Helpers | null>(null);
GlobalPictureInPictureContext.displayName = "GlobalPictureInPictureContext";

export const GlobalPictureInPictureProvider: React.FC = ({ children }) => {
  const { classes } = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [wherebyRoomUrl, setWherebyRoomUrl] = useState<string | undefined>(
    undefined
  );

  const open = useCallback((wherebyRoomUrl: string) => {
    setIsOpen(true);
    setWherebyRoomUrl(wherebyRoomUrl);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const helpers = useMemo<Helpers>(() => ({ open, close }), []);

  return (
    <GlobalPictureInPictureContext.Provider value={helpers}>
      {children}
      {isOpen && wherebyRoomUrl && (
        <Rnd
          onMouseDown={(e) => e.stopPropagation()}
          className={classes.pipResizableContent}
          default={{
            x: 800,
            y: 400,
            width: 765,
            height: 430,
          }}
          minWidth={400}
          minHeight={190}
          maxWidth={765}
          maxHeight={430}
          lockAspectRatio
          bounds="window"
        >
          <Box
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            padding={6}
          >
            <IconButton
              onClick={close}
              color="secondary"
              className={classes.closeButton}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box height="100%" width="100%">
              <iframe
                src={wherebyRoomUrl}
                allow="camera; microphone; speaker; fullscreen; display-capture"
                style={{ height: "100%", width: "100%" }}
              />
            </Box>
          </Box>
        </Rnd>
      )}
    </GlobalPictureInPictureContext.Provider>
  );
};

export default function useGlobalPictureInPicture(): Helpers {
  const value = useContext(GlobalPictureInPictureContext);

  if (value == null)
    throw new Error("GlobalPictureInPictureContext.Provider not found");

  return value;
}
